import { useEffect } from 'react';
import { OrderWithCustomer } from '../../types/orderTypes';
import { useProductManagement } from './useProductManagement';
import { useCustomerDetails } from './useCustomerDetails';
import { useOrderFormState } from './useOrderFormState';
import { useOrderSubmission } from '../useOrderSubmission';
import { useOrganizationData } from './useOrganizationData';
import { useShopData } from './useShopData';
import { useCustomerSelection } from './useCustomerSelection';
import { useOrderNumberGenerator } from './useOrderNumberGenerator';

export const useOrderForm = ({ 
  handleOrderAdded, 
  handleBackToOrders,
  shopId,
  organizationId: initialOrganizationId,
  currency = "DZD"
}: { 
  handleOrderAdded: (order: OrderWithCustomer) => void;
  handleBackToOrders: () => void;
  shopId?: string;
  organizationId?: string | null;
  currency?: string;
}) => {
  // Get organization ID from custom hook or use provided organizationId
  const { organizationId: hookOrganizationId } = useOrganizationData(initialOrganizationId);
  const organizationId = initialOrganizationId || hookOrganizationId;
  
  // Get shop data from custom hook
  const { shopId: validatedShopId } = useShopData(shopId);
  
  // Get auto-generated order number
  const { orderNumber: generatedOrderNumber, isLoading: isOrderNumberLoading } = useOrderNumberGenerator(shopId);
  
  // Initialize form state
  const {
    customerId,
    setCustomerId,
    customerName,
    setCustomerName,
    customerEmail,
    setCustomerEmail,
    customerPhone,
    setCustomerPhone,
    customerCountry,
    setCustomerCountry,
    customerRegion,
    setCustomerRegion,
    customerMunicipality,
    setCustomerMunicipality,
    orderNumber,
    setOrderNumber,
    orderDate,
    setOrderDate,
    shippingAddress,
    setShippingAddress,
    notes,
    setNotes,
    selectedCustomer,
    setSelectedCustomer
  } = useOrderFormState();
  
  // Mettre à jour le numéro de commande quand le générateur nous donne une valeur
  useEffect(() => {
    if (generatedOrderNumber && !orderNumber) {
      setOrderNumber(generatedOrderNumber);
    }
  }, [generatedOrderNumber, orderNumber, setOrderNumber]);
  
  // Initialize product management
  const {
    products,
    totalAmount,
    handleAddProduct,
    handleRemoveProduct,
    resetProducts
  } = useProductManagement();
  
  // Initialize customer details
  const { customerDetails, loading: loadingCustomerDetails } = useCustomerDetails(customerId);
  
  // Handle customer selection with custom hook
  const { handleSelectCustomer } = useCustomerSelection(
    customerId,
    setCustomerId,
    setCustomerName,
    setCustomerPhone,
    setCustomerEmail,
    setCustomerCountry,
    setCustomerRegion,
    setCustomerMunicipality,
    setShippingAddress,
    setSelectedCustomer
  );
  
  // Initialize order submission with shopId and currency explicitly passed
  const { isSubmitting, handleSubmit } = useOrderSubmission({
    customerId,
    customerName,
    customerEmail,
    customerPhone,
    customerCountry,
    customerRegion,
    customerMunicipality,
    orderNumber: orderNumber || generatedOrderNumber,
    orderDate,
    shippingAddress,
    notes,
    products,
    totalAmount,
    organizationId,
    handleOrderAdded,
    handleBackToOrders,
    shopId: validatedShopId,
    currency
  });
  
  return {
    // Organization
    organizationId,
    
    // Customer details
    customerId,
    customerName,
    customerEmail,
    customerPhone,
    customerCountry,
    customerRegion,
    customerMunicipality,
    selectedCustomer,
    
    // Order details
    orderNumber: orderNumber || generatedOrderNumber,
    orderDate,
    shippingAddress,
    notes,
    products,
    totalAmount,
    isSubmitting,
    isOrderNumberLoading,
    
    // Handlers
    setCustomerId,
    setCustomerName,
    setCustomerEmail,
    setCustomerPhone,
    setCustomerCountry,
    setCustomerRegion,
    setCustomerMunicipality,
    setOrderNumber,
    setOrderDate,
    setShippingAddress,
    setNotes,
    handleSelectCustomer,
    handleAddProduct,
    handleRemoveProduct,
    resetProducts,
    handleSubmit,
    setSelectedCustomer,
    currency
  };
};
